import classNames from "classnames";
import React, { useCallback, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Transaction } from "@common/types/transaction";
import { EagerLoaded } from "@common/types/base/orm";
import { AccrualMatchDisplay } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { pluralizeWithCount } from "@common/helpers/string";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useOverlays } from "contexts/OverlayContext";
import { useScope } from "@state/scopeContext.lib";
import { undoAccrualReview } from "@api/accrual-review.api";
import { invalidateAccrualMatchCaches } from "@components/accounting/accrualReview/accrualReviewCache";
import { AccrualMatchPreview } from "@components/accounting/accrualReview/AccrualMatchPreview";
import { aggregateMatches } from "@components/accounting/transactionMatch/aggregateMatches";
import { ClickableContainer } from "@design-system/ClickableContainer/ClickableContainer";
import { HStack } from "@design-system/HStack/HStack";
import { DetachIcon, SearchIcon } from "@icons";
import shellStyles from "@components/transactions/MatchedTransactionCard/MatchCardShell.module.scss";
import { RECEIPT_UPLOAD_HIGHLIGHTED_ELEMENT } from "../../ReceiptUpload/lib";
import styles from "./TransactionActionAccrualMatch.module.scss";

interface Props {
    transaction: EagerLoaded<Transaction, "transactionMatches"> & {
        accrualMatches?: AccrualMatchDisplay[];
    };
}

// Match IDs are unique within their own type (tx id is numeric, doc ids
// are uuids), so prefix with the source to avoid cross-type collisions.
// Schema guarantees at least one of the three is present; we still emit
// a stable string in the impossible empty case so React doesn't crash.
function getMatchKey(match: AccrualMatchDisplay): string {
    if (match.pairedTransaction) {
        return `tx-${match.pairedTransaction.id}`;
    }
    if (match.invoice) {
        return `inv-${match.invoice.id}`;
    }
    if (match.bill) {
        return `bill-${match.bill.id}`;
    }
    return `empty-${match.amount}`;
}

interface MatchRowProps {
    match: AccrualMatchDisplay;
    onClick: () => void;
}

const AccrualMatchRow: React.FC<MatchRowProps> = ({ match, onClick }) => {
    const aggregated = aggregateMatches([match]);
    if (aggregated.resolvedCount === 0) {
        return null;
    }

    return (
        <ClickableContainer className={styles.matchRow} onClick={onClick}>
            <AccrualMatchPreview
                counterpartyName={aggregated.firstName}
                extraCounterpartyCount={aggregated.extraNamesCount}
                amount={aggregated.totalAmount}
                countLabel={aggregated.countLabel}
            />
        </ClickableContainer>
    );
};

export const TransactionActionsAccrualMatch: React.FC<Props> = ({
    transaction,
}) => {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();
    const { openOverlay } = useOverlays();

    const accrualMatches = useMemo(
        () => transaction.accrualMatches ?? [],
        [transaction.accrualMatches],
    );

    const handleOpenDrawer = useCallback(() => {
        openOverlay("transactionMatch", { transactionId: transaction.id });
    }, [openOverlay, transaction.id]);

    // Per-transaction concurrency guard - prevents rapid double-clicks
    // from firing a second undoAccrualReview before the first completes.
    const isDetachingRef = useRef(false);

    const handleDetachAll = useCallback(async () => {
        if (!workspaceId || accrualMatches.length === 0) {
            return;
        }
        if (isDetachingRef.current) {
            return;
        }
        isDetachingRef.current = true;
        try {
            await undoAccrualReview({
                workspaceId,
                transactionId: transaction.id,
            });
            invalidateAccrualMatchCaches(queryClient, {
                transactionId: transaction.id,
            });
        } catch {
            // error toast handled by axios interceptor
        } finally {
            isDetachingRef.current = false;
        }
    }, [workspaceId, queryClient, transaction.id, accrualMatches.length]);

    const handleDetachClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            void handleDetachAll();
        },
        [handleDetachAll],
    );

    const handleEditClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            handleOpenDrawer();
        },
        [handleOpenDrawer],
    );

    if (accrualMatches.length === 0) {
        return (
            <div
                className={classNames(
                    shellStyles.shell,
                    RECEIPT_UPLOAD_HIGHLIGHTED_ELEMENT,
                    styles.transactionActionMatch,
                )}
            >
                <Tooltip content="Match this transaction to an invoice, bill, or transfer">
                    <div>
                        <HStack
                            as={ClickableContainer}
                            onClick={handleOpenDrawer}
                            justify="between"
                            align="center"
                            className={classNames(
                                "mb-0 cursor-pointer",
                                shellStyles.control,
                            )}
                        >
                            <span className={shellStyles.insideLabel}>
                                Find a match
                            </span>
                            <div className={shellStyles.iconsContainer}>
                                <div
                                    className={shellStyles.iconButton}
                                    aria-label="Find a match"
                                >
                                    <SearchIcon />
                                </div>
                            </div>
                        </HStack>
                    </div>
                </Tooltip>
            </div>
        );
    }

    const headerLabel = `Matched (${pluralizeWithCount(
        "match",
        accrualMatches.length,
    )})`;

    return (
        <div
            className={classNames(
                shellStyles.shell,
                styles.transactionActionMatch,
            )}
        >
            <HStack
                as={ClickableContainer}
                justify="between"
                align="center"
                className={classNames(
                    "mb-0 cursor-pointer",
                    shellStyles.control,
                    styles.cardHeader,
                )}
                onClick={handleOpenDrawer}
            >
                <span className={shellStyles.insideLabel}>{headerLabel}</span>
                <div className={shellStyles.iconsContainer}>
                    <Tooltip content="Unmatch all">
                        <button
                            type="button"
                            className={shellStyles.iconButton}
                            onClick={handleDetachClick}
                            aria-label="Unmatch all"
                        >
                            <DetachIcon />
                        </button>
                    </Tooltip>
                    <Tooltip content="Edit matches">
                        <button
                            type="button"
                            className={shellStyles.iconButton}
                            onClick={handleEditClick}
                            aria-label="Edit matches"
                        >
                            <SearchIcon />
                        </button>
                    </Tooltip>
                </div>
            </HStack>
            <div className={styles.rowsBody}>
                {accrualMatches.map((match) => (
                    <AccrualMatchRow
                        key={getMatchKey(match)}
                        match={match}
                        onClick={handleOpenDrawer}
                    />
                ))}
            </div>
        </div>
    );
};
